import NavMenu from '@/components/shared/NavMenu'
import PreviousMedicationRequestsList from '@/components/shared/PreviousMedicationRequestsList'
import StageStatus from '@/components/shared/stage-status'
import BackButton from '@/components/ui/BackButton'
import OrderSubscriptionTypeChip, { deriveOrderLabel } from '@/components/shared/OrderSubscriptionTypeChip'
import TabButton from '@/components/ui/TabButton'
import { ORDER_STATUS_LABELS, orderStatusLabelToValue, orderStatusValueToLabel } from '@/constants/statuses'
import { isReadOnly } from '@/lib/permissions'
import { u } from '@/lib/url'
import { formatDateTime, scrollToSection } from '@/lib/utils'
import { useInvestigationsCards } from '@/hooks/useInvestigationsCards'
import { useMedReqCards } from '@/hooks/useMedReqCards'
import { useOrderStatusStages } from '@/hooks/useOrderStatusStages'
import { useMedReqsByOrderHashQuery } from '@/services/med-requests'
import { useOrderByHashQuery, useUpdateOrderStatusMutation } from '@/services/orders'
import { useReviewQuestionnaire, useSendReviewQuestionnaire } from '@/services/reviewQuestionnaire'
import type { RefundMethod } from '@/types/order'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Alert, Box, Grid, Snackbar, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import InvestigationsList from '../../shared/InvestigationsList'
import OrderData from './sections/order-data'
import SendRefundDialog from './sections/SendRefundDialog'
import OrderTimeline from './sections/order-timeline'

const navItems = [
  { id: 'order-details', title: 'Order details' },
  { id: 'shipping', title: 'Shipping details' },
  { id: 'billing', title: 'Billing details' },
  { id: 'medication-requests', title: 'Medication requests' },
  { id: 'investigations', title: 'Investigations' }
]

export default function OrderDetailPage() {
  const { hash } = useParams<{ hash: string }>()
  if (!hash) {
    throw new Error('Order hash is required for order display')
  }

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showTimeline, setShowTimeline] = useState(false)
  const [refundDialogOpen, setRefundDialogOpen] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const readOnly = isReadOnly()
  const { data: order } = useOrderByHashQuery(hash)
  const { data: orderMedReqs = [] } = useMedReqsByOrderHashQuery(hash)
  const { medicationRequests, isLoading: isMedReqsLoading } = useMedReqCards({ orderHash: hash })
  const {
    stages,
    events: timelineEvents,
    isLoading: isStagesLoading
  } = useOrderStatusStages(hash, order.startedAt?.toISOString())
  const { investigations, isLoading: isInvestigationsLoading } = useInvestigationsCards({ orderHash: hash })

  // Only a subscription order can carry a review, so the availability call is
  // scoped to those rather than fired on every order page.
  const orderLabel = deriveOrderLabel(order.subscriptionType, order.orderNumber)
  const isSubscriptionOrder = orderLabel !== 'One-time order' && orderLabel !== 'Refund'
  const { data: reviewQuestionnaire } = useReviewQuestionnaire(hash, isSubscriptionOrder)
  const sendReviewQuestionnaireMutation = useSendReviewQuestionnaire()

  const handleSendReviewQuestionnaire = async () => {
    if (isReadOnly()) return

    // The API rejects a second open request for the same patient and
    // questionnaire, so a double click reports the one already open.
    const result = await sendReviewQuestionnaireMutation.mutateAsync(hash as string)

    enqueueSnackbar(result.alreadyOpen ? 'A review is already waiting for this patient' : 'Review questionnaire sent', {
      variant: result.alreadyOpen ? 'info' : 'success'
    })

    queryClient.invalidateQueries({ queryKey: ['review-questionnaire', hash] })
  }
  const updateStatusMutation = useUpdateOrderStatusMutation()

  const previousStatus = useMemo(() => {
    if (stages.length < 2) return undefined
    return stages[stages.length - 2].label
  }, [stages])

  const changedBy = useMemo(() => {
    const lastManual = [...timelineEvents]
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
      .filter(e => ['status_update', 'fields_update'].includes(e.noteType) && e.source === 'Manual')
      .at(-1)
    return lastManual?.user
  }, [timelineEvents])

  const blockedStatusOptions = useMemo(() => {
    const currentStatusLabel = stages.at(-1)?.label ?? orderStatusValueToLabel(order.status)
    const isAwaitingDecision = currentStatusLabel === 'Awaiting Decision'
    if (!isAwaitingDecision) return []

    // Require all med requests to have lastUpdatedBy with a name
    const allHaveApprover =
      orderMedReqs.length > 0 &&
      orderMedReqs.every(medReq => {
        return medReq.lastUpdatedBy?.name?.trim()
      })

    if (allHaveApprover) return []

    return [{ status: 'Awaiting Dispatch', reason: 'Approver information is missing' }]
  }, [stages, order.status, orderMedReqs])

  const handleStatusChange = async (stage: { label: string }) => {
    if (isReadOnly()) return
    const backendValue = orderStatusLabelToValue(stage.label)
    if (!backendValue) return

    await updateStatusMutation.mutateAsync({ hash, status: backendValue })
    queryClient.invalidateQueries({ queryKey: ['order', hash] })
    queryClient.invalidateQueries({ queryKey: ['timeline', 'order', hash] })
  }

  const handleTimelineToggle = () => {
    setShowTimeline(prev => !prev)
  }

  const handleRefundClick = () => {
    const evaroPHPURL = import.meta.env.VITE_EVARO_PHP_URL
    window.open(
      evaroPHPURL + u`/wp-admin/admin.php?page=wc-orders&s=${order.orderNumber}&search-filter=order_id`,
      '_blank'
    )
    // setRefundDialogOpen(true)
  }

  const handleRefundSubmit = async (data: { amount: number; method: RefundMethod; reason: string }) => {
    if (isReadOnly()) return
    await new Promise(resolve => setTimeout(resolve, 1500))
    console.log('Refund submitted:', data)
    setShowSuccess(true)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pb: 4 }}>
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity='success'
          sx={{ width: '100%' }}
          icon={<CheckCircleOutlineIcon sx={{ color: '#2E7D32' }} />}
        >
          Refund sent to patient
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <BackButton fallbackPath='/orders' defaultLabel='Back to Orders' />
          <Typography variant='pageTitle' sx={{ fontSize: 24 }}>
            Order
          </Typography>
          <OrderSubscriptionTypeChip subscriptionType={order.subscriptionType} orderNumber={order.orderNumber} />
          <TabButton active={showTimeline} onClick={handleTimelineToggle}>
            Timeline
          </TabButton>
          {!readOnly && (
            <TabButton active={refundDialogOpen} onClick={handleRefundClick}>
              Refund
            </TabButton>
          )}
          {!readOnly && reviewQuestionnaire?.available && (
            <TabButton
              active={false}
              onClick={handleSendReviewQuestionnaire}
              disabled={sendReviewQuestionnaireMutation.isPending || reviewQuestionnaire.requestStatus === 'pending'}
            >
              {reviewQuestionnaire.requestStatus === 'pending' ? 'Review already sent' : 'Send review questionnaire'}
            </TabButton>
          )}
        </Box>
      </Box>

      {showTimeline ? (
        <OrderTimeline orderNumber={order.orderNumber} orderHash={hash} />
      ) : (
        <>
          <StageStatus
            key={isStagesLoading ? 'loading' : 'timeline'}
            stages={stages}
            config={{
              title: 'Order status',
              statusOptions: ORDER_STATUS_LABELS,
              blockedStatusOptions,
              fallbackStatus: orderStatusValueToLabel(order.status),
              changedBy,
              updatedAt: order.updatedAt ? formatDateTime(order.updatedAt) : undefined,
              previousStatus
            }}
            onStageAdd={readOnly ? undefined : handleStatusChange}
            readOnly={readOnly}
          />

          <Grid container spacing={4}>
            <Grid size={9} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <OrderData order={order} readOnly={readOnly} />
              <PreviousMedicationRequestsList
                medicationRequests={medicationRequests}
                isLoading={isMedReqsLoading}
                sectionId='medication-requests'
                title='Medication requests'
                onItemClick={medId =>
                  navigate(`/med-requests/${medId}`, {
                    state: {
                      backLabel: 'Back to Order',
                      fallbackPath: `/orders/${hash}`
                    }
                  })
                }
              />
              <InvestigationsList
                investigations={investigations}
                isLoading={isInvestigationsLoading}
                sectionId='investigations'
                title='Investigations'
                onItemClick={investigationId =>
                  navigate(`/investigations/${investigationId}`, {
                    state: {
                      backLabel: 'Back to Order',
                      fallbackPath: `/orders/${hash}`
                    }
                  })
                }
              />
            </Grid>
            <Grid size={3} sx={{ position: 'sticky', top: 88, alignSelf: 'flex-start' }}>
              <NavMenu items={navItems} onNavigate={scrollToSection} />
            </Grid>
          </Grid>
        </>
      )}

      <SendRefundDialog
        open={refundDialogOpen}
        order={order}
        onClose={() => setRefundDialogOpen(false)}
        onSubmit={handleRefundSubmit}
      />
    </Box>
  )
}
